import { FormattedMessage } from 'react-intl';
import { OrderedMap } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import moment from 'moment';

import { BarButton } from '../Button';
import { brightenScreen, isMobile, resetScreen } from '../../../../mobile/helpers';
import { getCheckinCode } from './helpers';
import Content from '../Content';
import Empty from '../Empty';
import FetchModal from './FetchModal';
import Loader from '../Loader';
import LoginPrompt from '../LoginPrompt';
import Subheader from '../Subheader';
import TicketModal from './TicketModal';
import styles from './ticket-list.scss';

const propTypes = {
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    checkin_code: PropTypes.string.isRequired,
  })),
  event: PropTypes.shape({
    id: PropTypes.string,
    shortcode: PropTypes.string,
    purchase_url: PropTypes.string,
    local_end_time: PropTypes.string,
  }),
  loading: PropTypes.bool,
  user: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }),
  fadePast: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.shape({
      response: PropTypes.shape({}),
    }),
    PropTypes.bool,
  ]),
  onReload: PropTypes.func.isRequired,
};

class TicketList extends React.Component {
  constructor() {
    super();
    this.state = {
      ticketModalOpen: false,
      activeTicket: {},
      claimTicketModal: false,
    };
    this.code = null;
  }

  componentWillUnmount() {
    if (isMobile()) {
      resetScreen();
    }
  }

  openTicketModal = (ticket) => {
    this.setState({
      ticketModalOpen: true,
      activeTicket: ticket,
    }, () => {
      if (isMobile()) {
        brightenScreen();
      }
    });
  }

  closeTicketModal = () => {
    this.setState({
      ticketModalOpen: false,
    }, () => {
      if (isMobile()) {
        resetScreen();
      }
    });
  }

  openTicketLink = (url) => {
    window.open(url);
  }

  render() {
    const {
      tickets,
      loading,
      event,
      user,
      fadePast,
      error,
      onReload,
    } = this.props;
    const {
      ticketModalOpen,
      activeTicket,
      claimTicketModal,
    } = this.state;
    const ticketUrl = event && event.purchase_url;

    const sortedTickets = tickets && tickets.sort((a, b) => {
      // Sort by event.date_start then event.name, seat, byoc>non byoc and lastly ticket.id
      // This is important to keep the ticket list the same after re-render

      if (a.ticket_type?.event && b.ticket_type?.event) {
        const byStartDate = moment(b.ticket_type.event.local_start_time)
          .diff(moment(a.ticket_type.event.local_start_time));
        if (byStartDate !== 0) { return byStartDate; }

        const byEventName = a.ticket_type.event.name.localeCompare(b.ticket_type.event.name);
        if (byEventName !== 0) { return byEventName; }
      }

      if (a.expanded_seat && b.expanded_seat) {
        const byFloor = a.expanded_seat
          && b.expanded_seat
          && typeof a.expanded_seat.expanded_row.expanded_section.floor.name !== 'undefined'
          && typeof b.expanded_seat.expanded_row.expanded_section.floor.name !== 'undefined'
          && a.expanded_seat.expanded_row.expanded_section.floor.name.localeCompare(
            b.expanded_seat.expanded_row.expanded_section.floor.name,
          );
        if (byFloor !== 0 && byFloor !== false) { return byFloor; }

        const aSeat = `${a.expanded_seat.expanded_row.name}:${a.expanded_seat.name}`;
        const bSeat = `${b.expanded_seat.expanded_row.name}:${b.expanded_seat.name}`;
        const bySeat = aSeat.localeCompare(bSeat);
        if (bySeat !== 0) { return bySeat; }
      }

      // Make sure BYOC tickets are on top
      if (a.expanded_seat !== b.expanded_seat) {
        return (b.expanded_seat && 1) - (a.expanded_seat && 1);
      }

      return a.id.localeCompare(b.id);
    });

    const groupedTickets = sortedTickets && sortedTickets
      .filter((ticket) => !event || ticket.ticket_type.event.id === event.id)
      .reduce((acc, ticket) => {
        const group = ticket.ticket_type?.event && ticket.ticket_type?.event.name;
        return acc.set(group, [...(acc.get(group) || []), ticket]);
      }, new OrderedMap());

    /* eslint-disable react/no-array-index-key */
    return (
      <Content>
        <LoginPrompt
          prompt={!user}
          text="You need to log in or register an account to see your purchased tickets."
        />
        { user && (
          <BarButton
            icon="plus-circle"
            arrow
            onClick={() => this.setState({ claimTicketModal: true })}
          >
            Add missing ticket
          </BarButton>
        )}
        <Empty
          empty={user && !loading && (!tickets || !tickets?.length)}
          title="You don't seem to have any tickets on your account."
          message={ticketUrl
            && (
              <>
                <div>
                  What are you waiting for? Get your tickets now by pressing the button below.
                  Bought tickets but not seeing them? Make sure you connect your tickets to the
                  app through the email that was sent to you at the time of purchase.
                </div>
              </>
            )}
          button={ticketUrl
            ? {
              onClick: () => this.openTicketLink(ticketUrl),
              text: 'Buy your tickets now',
            }
            : undefined}
          error={error}
          onReload={onReload}
        />
        <Loader loading={user && loading} />

        { groupedTickets && user && groupedTickets.size > 0
          && (
          <div className={styles.tickets}>
            { groupedTickets.map((groupTickets, title) => (
              <React.Fragment key={title}>
                {title && <Subheader>{title}</Subheader>}
                { groupTickets.map((ticket) => (
                  <React.Fragment key={ticket.id}>
                    <div
                      className={classNames(
                        styles.preview,
                        {
                          [styles.past]: fadePast && moment()
                            .startOf('day')
                            .isAfter(
                              moment(
                                (event && event.local_end_time)
                                || ticket.ticket_type?.event.local_end_time,
                              ),
                            ),
                        },
                      )}
                      onClick={() => this.openTicketModal(ticket)}
                      role="button"
                      tabIndex="0"
                      key={ticket.id}
                    >
                      <div className={styles.info}>
                        <div className={styles.type}>
                          { ticket.ticket_type && ticket.ticket_type.name }
                          { ticket?.addons?.length > 0
                            && (
                              <span>
                                {' '}
                                <FormattedMessage
                                  id="tickets.ticket.addon.count"
                                  description="Addon count"
                                  defaultMessage={'with {count, number} {count, plural, one {addon} other {addons}}'}
                                  values={{
                                    count: ticket?.addons?.length,
                                  }}
                                />
                              </span>
                            )}
                        </div>
                        <div className={styles.name}>
                          {ticket.ticketHolder || '-'}
                        </div>
                        <div className={styles.seat}>
                          { !event && ticket.ticket_type
                            && (
                            <span>
                              <i className="far fa-calendar-alt" />
                              {' '}
                              {ticket.ticket_type.event.name}
                              {' '}
                            </span>
                            )}
                          { ticket.expanded_seat
                            && (
                              <>
                                <i className="far fa-map" />
                                {' '}
                                {
                                // If we're looking at a ticket for montreal 19 - use section name
                                // as floor name.
                                ((event && event.id === 'e04cdf59-11b4-4040-bb87-c25ca8678851')
                                  || (ticket.ticket_type?.event?.id === 'e04cdf59-11b4-4040-bb87-c25ca8678851')
                                )
                                  ? ticket.expanded_seat.expanded_row.expanded_section.name
                                  : ticket.expanded_seat.expanded_row.expanded_section.floor.name
                              }
                                <i className="far fa-map-marker" />
                                {' '}
                                {`${ticket.expanded_seat.expanded_row.name}:${ticket.expanded_seat.name}`}
                              </>
                            )}
                        </div>
                        {ticket?.claimed_by === user.id
                          && ticket?.order?.claimed_by !== user.id && (
                          <div
                            className={styles.owner}
                            title={'This ticket is only displayed here but someone else is the owner. '
                              + 'This is usually the case when you use the feature "Add missing ticket" within the app. '
                              + 'To become the owner, ask the current owner to "gift" the ticket to you at https://dreamhack.com/tickets'}
                          >
                            <i className="far fa-exclamation-triangle" /> Owned by {ticket.ticketOwner || 'someone else'}
                          </div>
                        )}

                        {ticket?.upgrades && (
                          <div className={styles.upgrades}>
                            {Object.values(ticket.upgrades || {}).map((u) => (
                              <div className={styles.upgrade}><i className="far fa-plus" />  {u.name}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      {getCheckinCode(ticket) && (
                        <div className={styles.qrButton}>
                          <i className="far fa-qrcode" />
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </React.Fragment>
            )).toArray()}
          </div>
          )}
        <FetchModal
          isOpen={claimTicketModal}
          onRequestClose={() => this.setState({ claimTicketModal: false })}
          user={user}
        />
        { user && activeTicket
          && (
            <TicketModal
              event={event}
              isOpen={ticketModalOpen}
              onRequestClose={() => this.closeTicketModal()}
              user={user}
              ticket={activeTicket}
              ticketCode={activeTicket && getCheckinCode(activeTicket)}
            />
          )}
      </Content>
    );
  }
}

TicketList.propTypes = propTypes;

export default TicketList;
